"use client";

import type { ReactNode } from "react";
import type { StripePaymentMethodMessagingElementOptions } from "@stripe/stripe-js";
import { cn } from "@/lib/utils";
import { formatPaymentAmount } from "@/utils/format";
import InstallmentMessage from "./InstallmentMessage";

type PriceSize = "sm" | "md" | "lg" | "xl";
type PriceAlign = "left" | "center" | "right" | "center-left" | "left-right";
type LabelPosition = "above" | "inline" | "none";

interface PriceWithInstallmentsProps {
  amount: number;
  currency?: string | null;
  label?: ReactNode;
  labelPosition?: LabelPosition;
  size?: PriceSize;
  align?: PriceAlign;
  showInstallments?: boolean;
  countryCode?: StripePaymentMethodMessagingElementOptions["countryCode"];
  installmentPaymentMethodTypes?: StripePaymentMethodMessagingElementOptions["paymentMethodTypes"];
  className?: string;
  priceClassName?: string;
  labelClassName?: string;
  lineClassName?: string;
  installmentClassName?: string;
}

const sizeClasses: Record<PriceSize, string> = {
  sm: "text-lg font-bold tracking-tight sm:text-xl",
  md: "text-xl font-bold tracking-tight sm:text-2xl",
  lg: "text-2xl font-bold tracking-tight sm:text-3xl",
  xl: "text-3xl font-bold tracking-tight sm:text-4xl",
};

const alignClasses: Record<
  PriceAlign,
  { root: string; line: string; installment: string }
> = {
  left: {
    root: "text-left",
    line: "justify-start",
    installment: "mr-auto",
  },
  center: {
    root: "text-center",
    line: "justify-center",
    installment: "mx-auto",
  },
  right: {
    root: "text-right",
    line: "justify-end",
    installment: "ml-auto",
  },
  "center-left": {
    root: "text-center lg:text-left",
    line: "justify-center lg:justify-start",
    installment: "mx-auto lg:mx-0",
  },
  "left-right": {
    root: "text-left sm:text-right",
    line: "justify-start sm:justify-end",
    installment: "mr-auto sm:ml-auto sm:mr-0",
  },
};

export default function PriceWithInstallments({
  amount,
  currency,
  label,
  labelPosition = "inline",
  size = "lg",
  align = "left",
  showInstallments = true,
  countryCode,
  installmentPaymentMethodTypes,
  className,
  priceClassName,
  labelClassName,
  lineClassName,
  installmentClassName,
}: PriceWithInstallmentsProps) {
  const numericAmount = Number(amount || 0);

  if (!Number.isFinite(numericAmount) || numericAmount <= 0) {
    return null;
  }

  const alignment = alignClasses[align];
  const installmentTextAlign =
    align === "right" ? "right" : align === "center" ? "center" : "left";
  const showInlineLabel = label && labelPosition === "inline";
  const showAboveLabel = label && labelPosition === "above";

  return (
    <div className={cn("min-w-0", alignment.root, className)}>
      {showAboveLabel && (
        <p
          className={cn(
            "text-xs font-semibold uppercase tracking-wide text-muted-foreground",
            labelClassName
          )}
        >
          {label}
        </p>
      )}

      <div
        className={cn(
          "flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-0.5",
          alignment.line,
          lineClassName
        )}
      >
        <span className={cn(sizeClasses[size], "text-primary", priceClassName)}>
          {formatPaymentAmount(numericAmount, currency)}
        </span>
        {showInlineLabel && (
          <span
            className={cn(
              "text-sm font-medium text-muted-foreground",
              labelClassName
            )}
          >
            {label}
          </span>
        )}
      </div>

      {showInstallments && (
        <InstallmentMessage
          amount={numericAmount}
          currency={currency}
          countryCode={countryCode}
          paymentMethodTypes={installmentPaymentMethodTypes}
          textAlign={installmentTextAlign}
          className={cn(
            "max-w-[18rem] text-[11px] leading-4 sm:text-xs",
            alignment.installment,
            installmentClassName
          )}
        />
      )}
    </div>
  );
}
